// tienda/TecnologiaSection.jsx
// Visual product tour using existing Fast Max platform previews.

import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  DatabaseZap,
  MapPinned,
  MessageSquareText,
  MonitorDot,
  ShieldCheck,
  Webhook,
} from "lucide-react";
import { brand, fadeUp } from "../shared/brand";

const PRODUCT_VIEWS = [
  {
    id: "control",
    tab: "Control operativo",
    icon: MonitorDot,
    title: "Toda la operación en una sola vista.",
    text: "Supervisa estados, entregas, incidencias y contraentrega sin reconstruir la operación entre hojas de cálculo y chats aislados.",
    image: "/dashboard.png",
    alt: "Vista demostrativa del panel operativo Fast Max",
    bullets: ["Estados centralizados", "Seguimiento por pedido", "Indicadores operativos"],
  },
  {
    id: "routes",
    tab: "Rutas en vivo",
    icon: MapPinned,
    title: "Visibilidad geográfica para actuar a tiempo.",
    text: "Consulta el avance de la operación y la ubicación disponible para detectar presión de ruta y responder con mejor contexto.",
    image: "/MapView.png",
    alt: "Vista demostrativa del mapa operativo Fast Max",
    bullets: ["Ubicación operativa", "Contexto de ruta", "Cobertura visible"],
  },
  {
    id: "communication",
    tab: "Comunicación",
    icon: MessageSquareText,
    title: "Cada conversación conectada con la entrega.",
    text: "Centraliza la comunicación operativa para que tiendas, equipo y clientes reciban información coherente durante el ciclo del pedido.",
    image: "/ChatView.png",
    alt: "Vista demostrativa de comunicación operativa Fast Max",
    bullets: ["Mensajes centralizados", "Contexto de entrega", "Menos seguimiento manual"],
  },
];

const INFRASTRUCTURE = [
  {
    icon: DatabaseZap,
    title: "Historial operativo",
    text: "Los cambios importantes conservan contexto para seguimiento, análisis y mejora continua.",
  },
  {
    icon: ShieldCheck,
    title: "Automatización controlada",
    text: "Reglas operativas ayudan a validar condiciones y reducir decisiones manuales repetitivas.",
  },
  {
    icon: Webhook,
    title: "APIs e integraciones",
    text: "Una base preparada para conectar pedidos, tracking y flujos de otros sistemas.",
  },
  {
    icon: BarChart3,
    title: "Datos accionables",
    text: "La información de la operación se convierte en visibilidad para tomar mejores decisiones.",
  },
];

export function TecnologiaSection() {
  const [activeViewId, setActiveViewId] = useState(PRODUCT_VIEWS[0].id);
  const activeView = PRODUCT_VIEWS.find(view => view.id === activeViewId) || PRODUCT_VIEWS[0];

  return (
    <section id="tecnologia" className="border-b scroll-mt-16" style={{ background: brand.bg, borderColor: brand.line }}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <motion.div {...fadeUp} className="mb-10 grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <p className="mb-3 text-xs font-black uppercase tracking-[0.2em]" style={{ color: brand.primary }}>
              Plataforma Fast Max
            </p>
            <h2 className="max-w-2xl text-3xl font-black leading-tight md:text-5xl" style={{ color: brand.ink }}>
              Tecnología visible en cada etapa de la entrega.
            </h2>
          </div>
          <p className="max-w-xl text-sm leading-relaxed md:text-base" style={{ color: brand.muted }}>
            No se trata solo de mover paquetes. Fast Max conecta operación, comunicación y datos para que
            cada pedido sea observable, coordinable y más fácil de escalar.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          className="overflow-hidden rounded-[1.75rem] border bg-white shadow-xl shadow-pink-950/5"
          style={{ borderColor: brand.line }}
        >
          <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
            <div className="flex flex-col border-b p-5 sm:p-7 lg:border-b-0 lg:border-r" style={{ borderColor: brand.line }}>
              <div className="grid grid-cols-3 gap-2 lg:grid-cols-1" role="tablist" aria-label="Vistas de la plataforma">
                {PRODUCT_VIEWS.map(view => {
                  const Icon = view.icon;
                  const isActive = activeView.id === view.id;

                  return (
                    <button
                      key={view.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="platform-preview"
                      onClick={() => setActiveViewId(view.id)}
                      className="flex min-w-0 items-center justify-center gap-2 rounded-xl border px-2 py-3 text-center text-[11px] font-black transition-colors sm:text-xs lg:justify-start lg:px-4 lg:text-left"
                      style={{
                        borderColor: isActive ? brand.primary : brand.line,
                        background: isActive ? `${brand.primary}0D` : "#FFFFFF",
                        color: isActive ? brand.primary : brand.muted,
                      }}
                    >
                      <Icon className="size-4 shrink-0" />
                      <span className="truncate">{view.tab}</span>
                    </button>
                  );
                })}
              </div>

              <motion.div
                key={activeView.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-7 flex-1"
              >
                <h3 className="text-2xl font-black leading-tight" style={{ color: brand.ink }}>{activeView.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: brand.muted }}>{activeView.text}</p>
                <ul className="mt-6 space-y-3">
                  {activeView.bullets.map(bullet => (
                    <li key={bullet} className="flex items-center gap-2 text-xs font-bold" style={{ color: brand.ink }}>
                      <span className="size-1.5 rounded-full" style={{ background: brand.accentGreen }} />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            <div id="platform-preview" role="tabpanel" className="relative min-h-[300px] overflow-hidden bg-slate-950 p-3 sm:p-5">
              <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                  background:
                    `radial-gradient(circle at 20% 20%, ${brand.primary}88, transparent 34%),` +
                    `radial-gradient(circle at 90% 5%, ${brand.accentGreen}55, transparent 28%)`,
                }}
              />
              <motion.div
                key={activeView.image}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="relative overflow-hidden rounded-2xl border border-white/15 bg-white shadow-2xl"
              >
                <img
                  src={`${process.env.PUBLIC_URL}${activeView.image}`}
                  alt={activeView.alt}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[3/2] size-full object-cover object-top"
                />
                <span className="absolute bottom-3 right-3 rounded-full border border-white/25 bg-black/55 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-white backdrop-blur">
                  Vista demostrativa
                </span>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {INFRASTRUCTURE.map(({ icon: Icon, title, text }, index) => (
            <motion.div
              key={title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: index * 0.05 }}
              className="rounded-2xl border bg-white p-5"
              style={{ borderColor: brand.line }}
            >
              <span
                className="mb-4 grid size-10 place-items-center rounded-xl"
                style={{ background: `${brand.primary}10`, color: brand.primary }}
              >
                <Icon className="size-5" />
              </span>
              <h3 className="text-sm font-black" style={{ color: brand.ink }}>{title}</h3>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: brand.muted }}>{text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
